import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const initialForm = {
  trial_identification: {
    public_title: '',
    scientific_title: '',
    secondary_ids: [{ issuing_authority: '', identifier_number: '' }],
    utn: '',
    acronym: '',
    linked_study_record: '',
  },
  health_condition: {
    conditions_studied: [''],
    condition_category_codes: [{ category: '', code: '' }],
  },
  interventions: {
    study_type: '',
    description: '',
    intervention_codes: [''],
    comparator_control_treatment: '',
    control_group_type: '',
  },
  outcomes: {
    primary: [{ name: '', assessment_method: '', timepoint: '' }],
    secondary: [{ name: '', assessment_method: '', timepoint: '' }],
  },
  eligibility: {
    key_inclusion_criteria: '',
    key_exclusion_criteria: '',
    minimum_age: '',
    maximum_age: '',
    sex: '',
    healthy_volunteers: false,
  },
  study_design: {
    purpose_of_study: '',
    allocation_to_intervention: '',
    phase: '',
    masking_blinding: '',
    assignment: '',
    endpoint_type: '',
    allocation_concealment: '',
    sequence_generation: '',
    observational_purpose: '',
    observational_duration: '',
    observational_selection: '',
    observational_timing: '',
    statistical_methods: '',
  },
  recruitment: {
    status: '',
    date_first_participant_enrolment: '',
    target_sample_size: '',
    recruitment_countries: [''],
    australia_states: [''],
    sites: [{ facility: '', city: '', state: '', country: '' }],
  },
  funding_and_sponsorship: {
    funding_sources: [''],
    primary_sponsor: '',
    secondary_sponsors: [''],
  },
  ethics_and_summary: {
    ethics_application_status: '',
    ethics_committees: [''],
    ethics_approval_date: '',
    trial_website: '',
    public_notes: '',
    private_notes: '',
    brief_summary: '',
  },
  contacts: {
    principal_investigator: {
      name: '',
      affiliation: '',
      telephone: '',
      email: '',
    },
    public_queries: {
      name: '',
      affiliation: '',
      telephone: '',
      email: '',
    },
    scientific_queries: {
      name: '',
      affiliation: '',
      telephone: '',
      email: '',
    },
  },
  ipd_sharing: {
    plan: '',
    description: '',
    when_available: '',
    access_criteria: '',
    data_location: '',
    what_data: '',
    available_to_whom: '',
    available_for_analyses: '',
    how_to_obtain: '',
  },
  study_results: [{ type: '', peer_reviewed: '', details: '' }],
};

const AustraliaNewZealandRegistry = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [validationErrors, setValidationErrors] = useState([]);

  // Defensive getters to avoid runtime errors when backend returns partial structures
  const primaryOutcomes = Array.isArray(formData.outcomes?.primary)
    ? formData.outcomes.primary
    : [];
  const secondaryOutcomes = Array.isArray(formData.outcomes?.secondary)
    ? formData.outcomes.secondary
    : [];
  const studyResults = Array.isArray(formData.study_results) ? formData.study_results : [];

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      try {
        const data = await protocolRegistrationService.getByProject(projectId);
        if (data?.anz_registry) {
          const anz = data.anz_registry;
          setFormData({
            ...initialForm,
            ...anz,
          });
        }
      } catch (error) {
        console.error('Error loading ANZ registry:', error);
        showSnackbar(error?.message || 'Failed to load ANZCTR registry data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const normalizePayload = (data) => ({
    anz_registry: data,
  });

  const validateForm = () => {
    const errors = [];

    const pushIfEmpty = (label, value) => {
      if (!value || String(value).trim() === '') errors.push(label);
    };

    // Mandatory ANZCTR-style fields (subset of * fields from PDF)
    pushIfEmpty('Public title', formData.trial_identification.public_title);
    pushIfEmpty('Scientific title', formData.trial_identification.scientific_title);
    pushIfEmpty('Primary health condition(s) studied', formData.health_condition.conditions_studied[0]);
    pushIfEmpty('Study type', formData.interventions.study_type);
    pushIfEmpty('Description of intervention(s) / exposure', formData.interventions.description);
    pushIfEmpty('Primary outcome name', primaryOutcomes[0]?.name);
    pushIfEmpty('Key inclusion criteria', formData.eligibility.key_inclusion_criteria);
    pushIfEmpty('Key exclusion criteria', formData.eligibility.key_exclusion_criteria);
    pushIfEmpty('Sex', formData.eligibility.sex);
    pushIfEmpty('Recruitment status', formData.recruitment.status);
    pushIfEmpty('Target sample size', formData.recruitment.target_sample_size);
    pushIfEmpty('Primary sponsor', formData.funding_and_sponsorship.primary_sponsor);
    pushIfEmpty('Ethics application status', formData.ethics_and_summary.ethics_application_status);
    pushIfEmpty('Principal investigator name', formData.contacts.principal_investigator.name);

    if (formData.ipd_sharing.plan === 'Yes') {
      pushIfEmpty('IPD sharing description', formData.ipd_sharing.description);
      pushIfEmpty('When IPD will be available', formData.ipd_sharing.when_available);
      pushIfEmpty('IPD access criteria', formData.ipd_sharing.access_criteria);
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    if (!validateForm()) {
      showSnackbar('Please fill all mandatory ANZCTR fields before saving.', 'error');
      return;
    }

    setSaving(true);
    try {
      await protocolRegistrationService.upsertByProject(projectId, normalizePayload(formData));
      showSnackbar('ANZCTR registry saved successfully', 'success');
    } catch (error) {
      console.error('Error saving ANZ registry:', error);
      showSnackbar(error?.message || 'Failed to save ANZCTR registry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoPopulate = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }
    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateRegistry(projectId, 'anz');
      if (response.success) {
        const regData = await protocolRegistrationService.getByProject(projectId);
        const anz = regData?.anz_registry || {};
        setFormData((prev) => ({ ...prev, ...anz }));
        showSnackbar('ANZCTR registry auto-populated from Core Datasheet. Review and save if needed.', 'success');
      } else {
        showSnackbar(response.message || 'Failed to auto-populate ANZCTR registry', 'error');
      }
    } catch (error) {
      console.error('Error auto-populating ANZ registry:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to auto-populate ANZCTR registry',
        'error'
      );
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleArrayFieldChange = (path, index, key, value) => {
    setFormData((prev) => {
      const segments = path.split('.');
      const updated = { ...prev };
      let cursor = updated;
      for (let i = 0; i < segments.length; i += 1) {
        const seg = segments[i];
        cursor[seg] = Array.isArray(cursor[seg]) ? [...cursor[seg]] : { ...cursor[seg] };
        if (i === segments.length - 1) {
          const arr = [...cursor[seg]];
          arr[index] = { ...arr[index], [key]: value };
          cursor[seg] = arr;
        } else {
          cursor = cursor[seg];
        }
      }
      return updated;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Australia &amp; New Zealand Clinical Trials Registry (ANZCTR)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage ANZCTR-specific registration fields. Core fields can be auto-populated from the
            Canonical Datasheet; please review and complete ANZCTR-only fields manually.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleAutoPopulate}
            disabled={autoPopulating || !projectId}
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate from Core Datasheet'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !projectId}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* 1. Titles & IDs */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              1. Titles &amp; IDs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Public Title"
                  value={formData.trial_identification.public_title}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        public_title: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Scientific Title"
                  value={formData.trial_identification.scientific_title}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        scientific_title: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Acronym"
                  value={formData.trial_identification.acronym}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        acronym: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="UTN"
                  value={formData.trial_identification.utn}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        utn: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 2. Health Condition */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              2. Health Condition
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Primary health condition(s) studied"
                  value={formData.health_condition.conditions_studied[0] || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      health_condition: {
                        ...prev.health_condition,
                        conditions_studied: [e.target.value],
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Condition category"
                  value={formData.health_condition.condition_category_codes[0]?.category || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      health_condition: {
                        ...prev.health_condition,
                        condition_category_codes: [
                          {
                            ...(prev.health_condition.condition_category_codes[0] || {}),
                            category: e.target.value,
                          },
                        ],
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Condition code"
                  value={formData.health_condition.condition_category_codes[0]?.code || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      health_condition: {
                        ...prev.health_condition,
                        condition_category_codes: [
                          {
                            ...(prev.health_condition.condition_category_codes[0] || {}),
                            code: e.target.value,
                          },
                        ],
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 3. Intervention / Exposure */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              3. Intervention / Exposure
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Study type"
                  select
                  value={formData.interventions.study_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interventions: {
                        ...prev.interventions,
                        study_type: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Interventional">Interventional</MenuItem>
                  <MenuItem value="Observational">Observational</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description of intervention(s) / exposure"
                  value={formData.interventions.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interventions: {
                        ...prev.interventions,
                        description: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Comparator / control treatment"
                  value={formData.interventions.comparator_control_treatment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interventions: {
                        ...prev.interventions,
                        comparator_control_treatment: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 4. Outcomes (Primary & Secondary) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              4. Outcomes
            </Typography>

            {/* Primary outcomes */}
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Primary outcome(s), assessment method and timepoint(s)
            </Typography>
            <Grid container spacing={2}>
              {primaryOutcomes.map((outcome, idx) => (
                <React.Fragment key={`primary-${idx}`}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={`Primary outcome ${idx + 1} name`}
                      value={outcome.name || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.primary)
                            ? prev.outcomes.primary
                            : [];
                          const primary = [...current];
                          primary[idx] = { ...primary[idx], name: e.target.value };
                          return { ...prev, outcomes: { ...prev.outcomes, primary } };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={`Primary outcome ${idx + 1} timepoint(s)`}
                      value={outcome.timepoint || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.primary)
                            ? prev.outcomes.primary
                            : [];
                          const primary = [...current];
                          primary[idx] = { ...primary[idx], timepoint: e.target.value };
                          return { ...prev, outcomes: { ...prev.outcomes, primary } };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={`Primary outcome ${idx + 1} assessment method`}
                      value={outcome.assessment_method || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.primary)
                            ? prev.outcomes.primary
                            : [];
                          const primary = [...current];
                          primary[idx] = { ...primary[idx], assessment_method: e.target.value };
                          return { ...prev, outcomes: { ...prev.outcomes, primary } };
                        })
                      }
                    />
                  </Grid>
                </React.Fragment>
              ))}
              <Grid item xs={12}>
                <Button
                  size="small"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                        outcomes: {
                          ...prev.outcomes,
                          primary: [
                            ...(Array.isArray(prev.outcomes?.primary) ? prev.outcomes.primary : []),
                            { name: '', assessment_method: '', timepoint: '' },
                          ],
                        },
                    }))
                  }
                >
                  Add primary outcome
                </Button>
              </Grid>
            </Grid>

            {/* Secondary outcomes */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Secondary outcome(s), assessment method and timepoint(s)
              </Typography>
              <Grid container spacing={2}>
                {secondaryOutcomes.map((outcome, idx) => (
                  <React.Fragment key={`secondary-${idx}`}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Secondary outcome ${idx + 1} name`}
                        value={outcome.name || ''}
                        onChange={(e) =>
                          setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.secondary)
                            ? prev.outcomes.secondary
                            : [];
                          const secondary = [...current];
                            secondary[idx] = { ...secondary[idx], name: e.target.value };
                            return { ...prev, outcomes: { ...prev.outcomes, secondary } };
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Secondary outcome ${idx + 1} timepoint(s)`}
                        value={outcome.timepoint || ''}
                        onChange={(e) =>
                          setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.secondary)
                            ? prev.outcomes.secondary
                            : [];
                          const secondary = [...current];
                            secondary[idx] = { ...secondary[idx], timepoint: e.target.value };
                            return { ...prev, outcomes: { ...prev.outcomes, secondary } };
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={`Secondary outcome ${idx + 1} assessment method`}
                        value={outcome.assessment_method || ''}
                        onChange={(e) =>
                          setFormData((prev) => {
                          const current = Array.isArray(prev.outcomes?.secondary)
                            ? prev.outcomes.secondary
                            : [];
                          const secondary = [...current];
                            secondary[idx] = { ...secondary[idx], assessment_method: e.target.value };
                            return { ...prev, outcomes: { ...prev.outcomes, secondary } };
                          })
                        }
                      />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12}>
                  <Button
                    size="small"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        outcomes: {
                          ...prev.outcomes,
                          secondary: [
                            ...(Array.isArray(prev.outcomes?.secondary) ? prev.outcomes.secondary : []),
                            { name: '', assessment_method: '', timepoint: '' },
                          ],
                        },
                      }))
                    }
                  >
                    Add secondary outcome
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* 5. Eligibility */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              5. Eligibility
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Key inclusion criteria"
                  value={formData.eligibility.key_inclusion_criteria}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        key_inclusion_criteria: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Key exclusion criteria"
                  value={formData.eligibility.key_exclusion_criteria}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        key_exclusion_criteria: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Minimum age"
                  value={formData.eligibility.minimum_age}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        minimum_age: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Maximum age"
                  value={formData.eligibility.maximum_age}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        maximum_age: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Sex"
                  select
                  value={formData.eligibility.sex}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        sex: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Males">Males</MenuItem>
                  <MenuItem value="Females">Females</MenuItem>
                  <MenuItem value="Both males and females">Both males and females</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 6. Recruitment & Sample Size */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              6. Recruitment &amp; Sample Size
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Recruitment status"
                  select
                  value={formData.recruitment.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recruitment: {
                        ...prev.recruitment,
                        status: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Not yet recruiting">Not yet recruiting</MenuItem>
                  <MenuItem value="Recruiting">Recruiting</MenuItem>
                  <MenuItem value="Active, not recruiting">Active, not recruiting</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Withdrawn">Withdrawn</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Stopped early">Stopped early</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  label="Date of first participant enrolment"
                  value={formData.recruitment.date_first_participant_enrolment || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recruitment: {
                        ...prev.recruitment,
                        date_first_participant_enrolment: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target sample size"
                  value={formData.recruitment.target_sample_size || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      recruitment: {
                        ...prev.recruitment,
                        target_sample_size: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 6b. Study Design (ANZCTR Step 6) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              6. Study Design
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Purpose of the study"
                  value={formData.study_design.purpose_of_study}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        purpose_of_study: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Treatment">Treatment</MenuItem>
                  <MenuItem value="Prevention">Prevention</MenuItem>
                  <MenuItem value="Diagnosis">Diagnosis</MenuItem>
                  <MenuItem value="Educational / counselling / training">
                    Educational / counselling / training
                  </MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Allocation to intervention"
                  value={formData.study_design.allocation_to_intervention}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        allocation_to_intervention: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Randomized">Randomised controlled trial</MenuItem>
                  <MenuItem value="Non-Randomized">Non-randomised trial</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Phase"
                  value={formData.study_design.phase}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        phase: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Not applicable">Not applicable</MenuItem>
                  <MenuItem value="Phase 0">Phase 0</MenuItem>
                  <MenuItem value="Phase 1">Phase 1</MenuItem>
                  <MenuItem value="Phase 1/Phase 2">Phase 1/Phase 2</MenuItem>
                  <MenuItem value="Phase 2">Phase 2</MenuItem>
                  <MenuItem value="Phase 2/Phase 3">Phase 2/Phase 3</MenuItem>
                  <MenuItem value="Phase 3">Phase 3</MenuItem>
                  <MenuItem value="Phase 3/Phase 4">Phase 3/Phase 4</MenuItem>
                  <MenuItem value="Phase 4">Phase 4</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Masking / blinding"
                  value={formData.study_design.masking_blinding}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        masking_blinding: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="OPEN LABEL">Open (masking not used)</MenuItem>
                  <MenuItem value="SINGLE BLIND">Single blind</MenuItem>
                  <MenuItem value="DOUBLE BLIND">Double blind</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Assignment"
                  value={formData.study_design.assignment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        assignment: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Single Group">Single group</MenuItem>
                  <MenuItem value="Parallel">Parallel</MenuItem>
                  <MenuItem value="Cross-over">Crossover</MenuItem>
                  <MenuItem value="Factorial">Factorial</MenuItem>
                  <MenuItem value="Sequential">Sequential</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Type of endpoint(s)"
                  value={formData.study_design.endpoint_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        endpoint_type: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Safety">Safety</MenuItem>
                  <MenuItem value="Efficacy">Efficacy</MenuItem>
                  <MenuItem value="Safety/efficacy">Safety/efficacy</MenuItem>
                  <MenuItem value="Bio-equivalence">Bio-equivalence</MenuItem>
                  <MenuItem value="Bio-availability">Bio-availability</MenuItem>
                  <MenuItem value="Pharmacokinetics">Pharmacokinetics</MenuItem>
                  <MenuItem value="Pharmacodynamics">Pharmacodynamics</MenuItem>
                  <MenuItem value="Pharmacokinetics / pharmacodynamics">
                    Pharmacokinetics / pharmacodynamics
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Statistical methods / analysis"
                  value={formData.study_design.statistical_methods}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_design: {
                        ...prev.study_design,
                        statistical_methods: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 7. Funding & Sponsors */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              7. Funding &amp; Sponsors
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Primary sponsor"
                  value={formData.funding_and_sponsorship.primary_sponsor}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      funding_and_sponsorship: {
                        ...prev.funding_and_sponsorship,
                        primary_sponsor: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Main funding source(s)"
                  value={formData.funding_and_sponsorship.funding_sources[0] || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      funding_and_sponsorship: {
                        ...prev.funding_and_sponsorship,
                        funding_sources: [e.target.value],
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 8. Ethics & Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              8. Ethics &amp; Brief Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ethics application status"
                  value={formData.ethics_and_summary.ethics_application_status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        ethics_application_status: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  label="Ethics approval date"
                  value={formData.ethics_and_summary.ethics_approval_date || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        ethics_approval_date: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ethics committee name (primary)"
                  value={formData.ethics_and_summary.ethics_committees[0] || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        ethics_committees: [e.target.value],
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Brief summary (lay description)"
                  value={formData.ethics_and_summary.brief_summary}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        brief_summary: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Trial website URL"
                  value={formData.ethics_and_summary.trial_website}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        trial_website: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Public notes"
                  value={formData.ethics_and_summary.public_notes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        public_notes: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Private notes (not publicly visible)"
                  value={formData.ethics_and_summary.private_notes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ethics_and_summary: {
                        ...prev.ethics_and_summary,
                        private_notes: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 9. Contacts */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              9. Contacts
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Principal investigator name"
                  value={formData.contacts.principal_investigator.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contacts: {
                        ...prev.contacts,
                        principal_investigator: {
                          ...prev.contacts.principal_investigator,
                          name: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Principal investigator email"
                  value={formData.contacts.principal_investigator.email}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contacts: {
                        ...prev.contacts,
                        principal_investigator: {
                          ...prev.contacts.principal_investigator,
                          email: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 10. Data Sharing (IPD) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              10. Data Sharing (IPD)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Plan to share IPD"
                  select
                  value={formData.ipd_sharing.plan}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        plan: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="IPD sharing description"
                  value={formData.ipd_sharing.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        description: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="What data will be shared?"
                  value={formData.ipd_sharing.what_data}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        what_data: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Available to whom?"
                  value={formData.ipd_sharing.available_to_whom}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        available_to_whom: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Available for what types of analyses?"
                  value={formData.ipd_sharing.available_for_analyses}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        available_for_analyses: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="How or where can data be obtained?"
                  value={formData.ipd_sharing.how_to_obtain}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        how_to_obtain: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 11. Study Results (basic) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              11. Study Results
            </Typography>
            <Grid container spacing={2}>
              {studyResults.map((res, idx) => (
                <React.Fragment key={`res-${idx}`}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Result type (e.g. Study results article, Basic results)"
                      value={res.type || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.study_results) ? prev.study_results : [];
                          const study_results = [...current];
                          study_results[idx] = { ...study_results[idx], type: e.target.value };
                          return { ...prev, study_results };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Peer reviewed? (Yes/No)"
                      value={res.peer_reviewed || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.study_results) ? prev.study_results : [];
                          const study_results = [...current];
                          study_results[idx] = { ...study_results[idx], peer_reviewed: e.target.value };
                          return { ...prev, study_results };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Details (DOI, citation, link or other details)"
                      value={res.details || ''}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const current = Array.isArray(prev.study_results) ? prev.study_results : [];
                          const study_results = [...current];
                          study_results[idx] = { ...study_results[idx], details: e.target.value };
                          return { ...prev, study_results };
                        })
                      }
                    />
                  </Grid>
                </React.Fragment>
              ))}
              <Grid item xs={12}>
                <Button
                  size="small"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      study_results: [
                        ...(Array.isArray(prev.study_results) ? prev.study_results : []),
                        { type: '', peer_reviewed: '', details: '' },
                      ],
                    }))
                  }
                >
                  Add study result
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AustraliaNewZealandRegistry;




